/**
 * Knowledge Graph Database Operations
 *
 * Server-safe data layer for knowledge_graph_nodes / knowledge_graph_edges.
 * Nodes are keyed by (workspace_id, node_type, source_id); edges by
 * (workspace_id, from_node_id, to_node_id, edge_type). Upserts are idempotent
 * so indexing hooks can re-run without duplication.
 *
 * Primary responsibilities:
 * - Upsert nodes/edges, fetch a node, list neighbors, query + delete
 *
 * Dependencies:
 * - lib/knowledge-graph/types (typed row shapes)
 * - Service client on server, browser client only in the browser
 *
 * Note: supabase types.ts is not regenerated for these tables yet, so table
 * access is cast through a loose builder. Row shapes map into typed types.
 */

import { createServiceClient } from '@/lib/supabase/service-client'
import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from '@/supabase/types'
import {
  KnowledgeGraphEdgeRow,
  KnowledgeGraphNodeRow,
  KnowledgeGraphSemanticMatchRow,
  KnowledgeGraphTraverseRow
} from '@/db/knowledge-graph-rows'
import {
  EdgeType,
  EdgeUpsert,
  GraphMetadata,
  GraphNeighbor,
  GraphQueryParams,
  GraphQueryResult,
  KnowledgeGraphEdge,
  KnowledgeGraphNode,
  NeighborsOptions,
  NodeType,
  NodeUpsert,
  SemanticMatch
} from '@/lib/knowledge-graph/types'

// --------------

/** Use service client on server when no client passed; browser client only on client. */
function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return require('@/lib/supabase/browser-client').supabase as SupabaseClient<Database>
}

// --------------

// types not regenerated for knowledge_graph_*: table access is untyped, so we
// route through a loose builder. Terminal calls resolve to { data, error };
// intermediate chain methods return the same builder.
interface QueryResult {
  data: unknown
  error: unknown
}

interface LooseBuilder extends PromiseLike<QueryResult> {
  insert: (values: unknown) => LooseBuilder
  upsert: (values: unknown, opts?: { onConflict?: string }) => LooseBuilder
  select: (columns?: string) => LooseBuilder
  update: (values: unknown) => LooseBuilder
  delete: () => LooseBuilder
  eq: (column: string, value: unknown) => LooseBuilder
  in: (column: string, values: unknown[]) => LooseBuilder
  ilike: (column: string, pattern: string) => LooseBuilder
  is: (column: string, value: unknown) => LooseBuilder
  or: (filters: string) => LooseBuilder
  order: (column: string, opts?: { ascending?: boolean }) => LooseBuilder
  limit: (count: number) => LooseBuilder
  single: () => Promise<QueryResult>
  maybeSingle: () => Promise<QueryResult>
}

interface UntypedClient {
  from: (table: string) => LooseBuilder
  rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<QueryResult>
}

function untyped(client: SupabaseClient<Database>): UntypedClient {
  return client as unknown as UntypedClient
}

// --------------

// Row shapes are defined authoritatively in db/knowledge-graph-rows.ts. Alias
// them locally so the mappers/read paths below read naturally. The generated
// Database type does not yet include these tables (see untyped() below).
type RawNode = KnowledgeGraphNodeRow
type RawEdge = KnowledgeGraphEdgeRow


// --------------

function mapNode(raw: RawNode): KnowledgeGraphNode {
  return {
    id: raw.id,
    workspaceId: raw.workspace_id,
    nodeType: raw.node_type as NodeType,
    sourceId: raw.source_id,
    title: raw.title,
    summary: raw.summary,
    metadata: raw.metadata ?? {},
    contentHash: raw.content_hash ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at
  }
}

// --------------

function mapEdge(raw: RawEdge): KnowledgeGraphEdge {
  return {
    id: raw.id,
    workspaceId: raw.workspace_id,
    fromNodeId: raw.from_node_id,
    toNodeId: raw.to_node_id,
    edgeType: raw.edge_type as EdgeType,
    weight: raw.weight,
    metadata: raw.metadata ?? {},
    createdAt: raw.created_at
  }
}

// --------------

const NODES_TABLE = 'knowledge_graph_nodes'
const EDGES_TABLE = 'knowledge_graph_edges'

// --------------

/** Idempotent node upsert keyed by (workspace_id, node_type, source_id). */
export async function upsertNode(
  input: NodeUpsert,
  supabaseClient?: SupabaseClient<Database>
): Promise<KnowledgeGraphNode> {
  const client = untyped(getDefaultClient(supabaseClient))

  const row = {
    workspace_id: input.workspaceId,
    node_type: input.nodeType,
    source_id: input.sourceId,
    title: input.title ?? null,
    summary: input.summary ?? null,
    metadata: input.metadata ?? {},
    updated_at: new Date().toISOString()
  }

  const { data, error } = await client
    .from(NODES_TABLE)
    .upsert(row, { onConflict: 'workspace_id,node_type,source_id' })
    .select('*')
    .single()

  if (error) throw error
  return mapNode(data as RawNode)
}

// --------------

/** Idempotent edge upsert keyed by (workspace, from, to, edge_type). */
export async function upsertEdge(
  input: EdgeUpsert,
  supabaseClient?: SupabaseClient<Database>
): Promise<KnowledgeGraphEdge> {
  const client = untyped(getDefaultClient(supabaseClient))

  const row = {
    workspace_id: input.workspaceId,
    from_node_id: input.fromNodeId,
    to_node_id: input.toNodeId,
    edge_type: input.edgeType,
    weight: input.weight ?? 1,
    metadata: input.metadata ?? {}
  }

  const { data, error } = await client
    .from(EDGES_TABLE)
    .upsert(row, { onConflict: 'workspace_id,from_node_id,to_node_id,edge_type' })
    .select('*')
    .single()

  if (error) throw error
  return mapEdge(data as RawEdge)
}

// --------------

/** Fetch a single node by workspace + type + source id. */
export async function getNode(
  params: { workspaceId: string; nodeType: NodeType; sourceId: string },
  supabaseClient?: SupabaseClient<Database>
): Promise<KnowledgeGraphNode | null> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client
    .from(NODES_TABLE)
    .select('*')
    .eq('workspace_id', params.workspaceId)
    .eq('node_type', params.nodeType)
    .eq('source_id', params.sourceId)
    .maybeSingle()

  if (error) throw error
  if (!data) return null
  return mapNode(data as RawNode)
}

// --------------

/** Fetch a single node by primary key. */
export async function getNodeById(
  nodeId: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<KnowledgeGraphNode | null> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client
    .from(NODES_TABLE)
    .select('*')
    .eq('id', nodeId)
    .maybeSingle()

  if (error) throw error
  if (!data) return null
  return mapNode(data as RawNode)
}

// --------------

/**
 * List neighbors of a node in BOTH directions: outgoing edges (this node is the
 * source) and incoming edges (this node is the target). For each edge the
 * neighbor is the node at the OTHER end, tagged with the traversal direction.
 * Optionally filtered by edge type. This makes the graph truly navigable —
 * e.g. from a contact you can reach the lead it converted from, and from that
 * lead you can reach the contact.
 */
export async function getNeighbors(
  nodeId: string,
  options: NeighborsOptions = {},
  supabaseClient?: SupabaseClient<Database>
): Promise<GraphNeighbor[]> {
  const client = untyped(getDefaultClient(supabaseClient))
  const limit = options.limit ?? 100

  const runEdgeQuery = async (column: 'from_node_id' | 'to_node_id') => {
    let edgeQuery = client.from(EDGES_TABLE).select('*').eq(column, nodeId)
    if (options.edgeTypes && options.edgeTypes.length > 0) {
      edgeQuery = edgeQuery.in('edge_type', options.edgeTypes)
    }
    edgeQuery = edgeQuery.limit(limit)
    const { data, error } = await edgeQuery
    if (error) throw error
    return (data as RawEdge[]).map(mapEdge)
  }

  const [outgoing, incoming] = await Promise.all([
    runEdgeQuery('from_node_id'),
    runEdgeQuery('to_node_id')
  ])

  // Direction is relative to the node we expanded from: for outgoing edges the
  // neighbor is the edge target; for incoming edges it is the edge source.
  const directed: { edge: KnowledgeGraphEdge; neighborId: string; direction: 'outgoing' | 'incoming' }[] = []
  for (const edge of outgoing) {
    directed.push({ edge, neighborId: edge.toNodeId, direction: 'outgoing' })
  }
  for (const edge of incoming) {
    // A self-loop already surfaced as outgoing; skip the duplicate.
    if (edge.fromNodeId === nodeId && edge.toNodeId === nodeId) continue
    directed.push({ edge, neighborId: edge.fromNodeId, direction: 'incoming' })
  }

  if (directed.length === 0) return []

  const neighborIds = [...new Set(directed.map(d => d.neighborId))]
  const { data: nodeData, error: nodeError } = await client
    .from(NODES_TABLE)
    .select('*')
    .in('id', neighborIds)

  if (nodeError) throw nodeError

  const nodeById = new Map<string, KnowledgeGraphNode>()
  for (const raw of nodeData as RawNode[]) {
    const node = mapNode(raw)
    nodeById.set(node.id, node)
  }

  const neighbors: GraphNeighbor[] = []
  const seenEdgeIds = new Set<string>()
  for (const { edge, neighborId, direction } of directed) {
    if (seenEdgeIds.has(edge.id)) continue
    const node = nodeById.get(neighborId)
    if (node) {
      seenEdgeIds.add(edge.id)
      neighbors.push({ node, edge, direction })
    }
  }
  return neighbors
}

// --------------

/** Query nodes in a workspace, optionally by type + free-text search. */
export async function queryGraph(
  params: GraphQueryParams,
  supabaseClient?: SupabaseClient<Database>
): Promise<GraphQueryResult> {
  const client = untyped(getDefaultClient(supabaseClient))
  const limit = params.limit ?? 50

  let query = client.from(NODES_TABLE).select('*').eq('workspace_id', params.workspaceId)

  if (params.nodeType) {
    query = query.eq('node_type', params.nodeType)
  }

  if (params.search && params.search.trim().length > 0) {
    const term = params.search.trim().replace(/[%,]/g, '')
    query = query.or(`title.ilike.%${term}%,summary.ilike.%${term}%`)
  }

  query = query.order('updated_at', { ascending: false }).limit(limit)

  const { data, error } = await query
  if (error) throw error

  return { nodes: (data as RawNode[]).map(mapNode) }
}

// --------------

/**
 * Delete all nodes for a given source (edges cascade via FK). Used when a
 * source entity is removed so the graph stays consistent.
 */
export async function deleteNodesForSource(
  params: { workspaceId: string; nodeType: NodeType; sourceId: string },
  supabaseClient?: SupabaseClient<Database>
): Promise<void> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { error } = await client
    .from(NODES_TABLE)
    .delete()
    .eq('workspace_id', params.workspaceId)
    .eq('node_type', params.nodeType)
    .eq('source_id', params.sourceId)

  if (error) throw error
}

// --------------

/**
 * Persist a node's embedding vector + the content hash that produced it, so
 * the pipeline can skip re-embedding unchanged nodes. Stamps embedded_at.
 */
export async function updateNodeEmbedding(
  nodeId: string,
  embedding: number[],
  contentHash: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<void> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { error } = await client
    .from(NODES_TABLE)
    .update({
      embedding: embedding as unknown,
      content_hash: contentHash,
      embedded_at: new Date().toISOString()
    })
    .eq('id', nodeId)

  if (error) throw error
}

// --------------

type RawSemanticMatch = KnowledgeGraphSemanticMatchRow

/**
 * Semantic nearest-neighbor search over node embeddings via the
 * match_knowledge_graph_nodes RPC. Returns nodes ranked by cosine similarity,
 * excluding an optional node id (usually the traversal root).
 */
export async function matchNodesByEmbedding(
  params: {
    workspaceId: string
    queryEmbedding: number[]
    matchCount?: number
    similarityThreshold?: number
    excludeNodeId?: string | null
  },
  supabaseClient?: SupabaseClient<Database>
): Promise<SemanticMatch[]> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client.rpc('match_knowledge_graph_nodes', {
    p_workspace_id: params.workspaceId,
    p_query_embedding: params.queryEmbedding as unknown,
    p_match_count: params.matchCount ?? 10,
    p_similarity_threshold: params.similarityThreshold ?? 0.7,
    p_exclude_node_id: params.excludeNodeId ?? null
  })

  if (error) throw error

  const rows = (data as RawSemanticMatch[] | null) ?? []
  return rows.map(row => ({
    node: mapNode(row),
    similarity: typeof row.similarity === 'number' ? row.similarity : 0
  }))
}

// --------------

/**
 * Read a node's stored embedding vector (for semantic expansion from a known
 * root). Returns null when the node has no embedding yet. The vector column is
 * returned by pgvector as a string like "[0.1,0.2,...]"; parse to number[].
 */
export async function getNodeEmbedding(
  nodeId: string,
  supabaseClient?: SupabaseClient<Database>
): Promise<number[] | null> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client
    .from(NODES_TABLE)
    .select('embedding')
    .eq('id', nodeId)
    .maybeSingle()

  if (error) throw error
  const raw = (data as { embedding?: unknown } | null)?.embedding
  return parseEmbedding(raw)
}

/** pgvector may return a number[] (json) or a "[...]" string; normalize both. */
function parseEmbedding(raw: unknown): number[] | null {
  if (Array.isArray(raw)) {
    return raw.every(n => typeof n === 'number') ? (raw as number[]) : null
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.every(n => typeof n === 'number')) {
        return parsed as number[]
      }
    } catch {
      return null
    }
  }
  return null
}

// --------------

/**
 * Merge a duplicate node into a canonical one: repoint every edge that touches
 * the duplicate to the canonical node, record a `same_as` edge (canonical ->
 * duplicate) for provenance, and mark the duplicate merged in metadata. Edge
 * repointing is idempotent — re-running merges the same pair with no new rows.
 *
 * Note: this repoints edges rather than deleting the duplicate node, so history
 * and any direct references to the duplicate id remain resolvable. A follow-up
 * sweep can archive merged duplicates once callers stop referencing them.
 */
export async function mergeNodes(
  params: { workspaceId: string; canonicalNodeId: string; duplicateNodeId: string },
  supabaseClient?: SupabaseClient<Database>
): Promise<{ repointed: number }> {
  const { workspaceId, canonicalNodeId, duplicateNodeId } = params
  if (canonicalNodeId === duplicateNodeId) return { repointed: 0 }

  const client = untyped(getDefaultClient(supabaseClient))

  // Load every edge touching the duplicate (either endpoint), then rewrite the
  // duplicate endpoint to the canonical node. Self-loops that would result are
  // dropped. Upsert (idempotent) so a repeated merge does not duplicate edges.
  const [{ data: outData, error: outErr }, { data: inData, error: inErr }] = await Promise.all([
    client.from(EDGES_TABLE).select('*').eq('from_node_id', duplicateNodeId),
    client.from(EDGES_TABLE).select('*').eq('to_node_id', duplicateNodeId)
  ])
  if (outErr) throw outErr
  if (inErr) throw inErr

  const touching = [...((outData as RawEdge[]) ?? []), ...((inData as RawEdge[]) ?? [])]
  let repointed = 0

  for (const raw of touching) {
    const from = raw.from_node_id === duplicateNodeId ? canonicalNodeId : raw.from_node_id
    const to = raw.to_node_id === duplicateNodeId ? canonicalNodeId : raw.to_node_id
    if (from === to) continue // dropped self-loop

    const { error: upErr } = await client
      .from(EDGES_TABLE)
      .upsert(
        {
          workspace_id: workspaceId,
          from_node_id: from,
          to_node_id: to,
          edge_type: raw.edge_type,
          weight: raw.weight ?? 1,
          metadata: raw.metadata ?? {}
        },
        { onConflict: 'workspace_id,from_node_id,to_node_id,edge_type' }
      )
    if (upErr) throw upErr
    repointed += 1
  }

  // Record the canonical <- duplicate identity link.
  await upsertEdge(
    {
      workspaceId,
      fromNodeId: canonicalNodeId,
      toNodeId: duplicateNodeId,
      edgeType: 'same_as',
      weight: 10,
      metadata: { merged: true }
    },
    supabaseClient
  )

  // Mark the duplicate as merged so callers can prefer the canonical node.
  const { error: markErr } = await client
    .from(NODES_TABLE)
    .update({ metadata: { mergedInto: canonicalNodeId }, updated_at: new Date().toISOString() })
    .eq('id', duplicateNodeId)
  if (markErr) throw markErr

  return { repointed }
}

// --------------

type RawTraverseRow = KnowledgeGraphTraverseRow

export interface SqlTraverseResult {
  nodes: KnowledgeGraphNode[]
  edges: KnowledgeGraphEdge[]
  /** node id -> depth/best-weight the CTE reached it at (for ranking). */
  reach: Record<string, { depth: number; bestWeight: number }>
}

/**
 * Single-round-trip recursive traversal via the traverse_knowledge_graph RPC.
 * Returns reached nodes (with depth + best weight) and the connecting edges.
 * Throws if the RPC is unavailable so callers can fall back to BFS.
 */
export async function traverseGraphSql(
  params: { workspaceId: string; rootNodeId: string; depth?: number; maxNodes?: number },
  supabaseClient?: SupabaseClient<Database>
): Promise<SqlTraverseResult> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client.rpc('traverse_knowledge_graph', {
    p_workspace_id: params.workspaceId,
    p_root_node_id: params.rootNodeId,
    p_depth: params.depth ?? 2,
    p_max_nodes: params.maxNodes ?? 200
  })

  if (error) throw error

  const rows = (data as RawTraverseRow[] | null) ?? []
  const nodes: KnowledgeGraphNode[] = []
  const edges: KnowledgeGraphEdge[] = []
  const reach: Record<string, { depth: number; bestWeight: number }> = {}

  for (const row of rows) {
    if (row.kind === 'node' && row.node_id) {
      nodes.push(
        mapNode({
          id: row.node_id,
          workspace_id: params.workspaceId,
          node_type: row.node_type ?? 'memory',
          source_id: row.source_id ?? '',
          title: row.title,
          summary: row.summary,
          metadata: row.metadata,
          content_hash: null,
          embedded_at: null,
          created_at: row.created_at ?? '',
          updated_at: row.updated_at ?? ''
        })
      )
      reach[row.node_id] = {
        depth: row.depth ?? 0,
        bestWeight: row.best_weight ?? 0
      }
    } else if (row.kind === 'edge' && row.edge_id) {
      edges.push(
        mapEdge({
          id: row.edge_id,
          workspace_id: params.workspaceId,
          from_node_id: row.from_node_id ?? '',
          to_node_id: row.to_node_id ?? '',
          edge_type: row.edge_type ?? 'linked_to',
          weight: row.edge_weight ?? 1,
          metadata: {},
          created_at: ''
        })
      )
    }
  }

  return { nodes, edges, reach }
}

// --------------

/**
 * List nodes in a workspace that have no embedding yet (for backfill). Returns
 * the minimal shape the embedding backfill needs. Bounded by `limit`.
 */
export async function listUnembeddedNodes(
  workspaceId: string,
  limit: number,
  supabaseClient?: SupabaseClient<Database>
): Promise<
  Array<{ id: string; nodeType: string; title: string | null; summary: string | null; contentHash: string | null }>
> {
  const client = untyped(getDefaultClient(supabaseClient))

  const { data, error } = await client
    .from(NODES_TABLE)
    .select('id, node_type, title, summary, content_hash')
    .eq('workspace_id', workspaceId)
    .is('embedding', null)
    .limit(limit)

  if (error) throw error

  const rows = (data as Array<Pick<RawNode, 'id' | 'node_type' | 'title' | 'summary' | 'content_hash'>>) ?? []
  return rows.map(row => ({
    id: row.id,
    nodeType: row.node_type,
    title: row.title,
    summary: row.summary,
    contentHash: row.content_hash ?? null
  }))
}
